import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type { InsightsLabV2Favorite } from 'api/insightsLabV2'
import ChevronRight from 'components/Icons/ChevronRight'
import Star from 'components/Icons/Star'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'

const t = buildTranslate('insights_lab_v2.picker')
// `open_report_named` ("Open %{title}") lives at the top-level
// insights_lab_v2 namespace (shared with WorkspaceSearch's identical
// "open this named report" control) rather than under `.picker` — reused
// here instead of inventing a picker-scoped duplicate.
const tRoot = buildTranslate('insights_lab_v2')

// Reserve a constant height for the action column so toggling between the
// star and the inline remove-confirmation (taller uikit `size="small"`
// buttons) never changes the row height. Matches the small Button height so
// the confirmation fits without growing the row.
const REMOVE_ACTION_HEIGHT = '32px'

type Props = {
  favorite: InsightsLabV2Favorite
  // Whether this row's remove-confirmation is the one currently showing.
  // Owned by the parent so only one row confirms removal at a time.
  isConfirming: boolean
  onOpenFavorite: (favorite: InsightsLabV2Favorite) => void
  onRequestRemove: () => void
  onConfirmRemove: () => void
  onCancelRemove: () => void
}

// A single saved-favorite row: opens the favorite on click, with a star that
// reveals an inline "Remove this favorite?" confirmation (rather than
// deleting immediately).
const FavoriteRow = ({
  favorite,
  isConfirming,
  onOpenFavorite,
  onRequestRemove,
  onConfirmRemove,
  onCancelRemove,
}: Props): JSX.Element => {
  const [css] = useStyletron()

  return (
    // Matches ReportButton's row container exactly (grid/divider/hover) so
    // Favorites reads as the same list style as every other report list in
    // this browser. Set explicitly via css() rather than Bootstrap's
    // `d-block`/`border-0`/`bg-transparent` utilities, which compile with
    // `!important` and would silently beat the display/border/background
    // rules below (see ReportButton.tsx for the bug this mirrors/avoids).
    <div
      className={`p-m ${css({
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        gap: '16px',
        // Fully longhand per side (width/style/color), matching this repo's
        // established convention for a styletron border reset (see
        // `solidBorder` in src/scenes/LiveUpdates/components/styleHelpers.ts)
        // rather than mixing the `border` shorthand with `borderBottom` —
        // atomic CSS class insertion order, not CSS cascade rules, decides
        // which one wins, so that combination is fragile (see
        // context/knowledge/styletron-css-cascade.md).
        borderTopWidth: '0',
        borderTopStyle: 'none',
        borderTopColor: 'transparent',
        borderRightWidth: '0',
        borderRightStyle: 'none',
        borderRightColor: 'transparent',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: palette.grey20,
        borderLeftWidth: '0',
        borderLeftStyle: 'none',
        borderLeftColor: 'transparent',
        backgroundColor: 'transparent',
        ':hover': {
          backgroundColor: palette.grey10,
        },
      })}`}
    >
      <button
        type="button"
        // `border-0` removes the default focus ring; restore a visible
        // focus indicator for keyboard users (WCAG 2.4.7). Unlike the outer
        // container above, this button doesn't drive any display/border/
        // background via css(), so the Bootstrap utility resets here don't
        // fight styletron and are left as-is.
        className={`text-left border-0 bg-transparent ${css({
          ':focus-visible': {
            outline: `2px solid ${palette.belizeHole}`,
            outlineOffset: '2px',
          },
        })}`}
        onClick={() => onOpenFavorite(favorite)}
      >
        <Text.BodyText3 bold tag="div">
          {favorite.name}
        </Text.BodyText3>
        {favorite.description && (
          <Text.BodyText5 color="grey70">{favorite.description}</Text.BodyText5>
        )}
      </button>
      {/* Fixed-height action column so toggling the star <-> the
          inline confirmation (taller buttons) never resizes the row. Right
          edge alignment/spacing comes from the row's own `p-m` padding,
          matching how ReportButton right-aligns its trailing content. */}
      <div
        className={`d-flex align-items-center justify-content-end ${css({
          minHeight: REMOVE_ACTION_HEIGHT,
        })}`}
      >
        {isConfirming ? (
          <div
            // Announce the inline confirmation as a decision that
            // requires a choice, and label it for screen readers
            // (WCAG 1.3.1 Info and Relationships).
            role="alertdialog"
            aria-live="polite"
            aria-label={t('confirm_remove')}
            className="d-flex align-items-center"
          >
            <Text.BodyText5 color="grey70" className="mr-xs">
              {t('confirm_remove')}
            </Text.BodyText5>
            {/* Destructive action first. uikit Button has no red/
                destructive kind in this theme (negative/warning all
                fall back to primary blue), so style it with cayenne50
                — the design system's destructive/error red, the same
                token the rest of the app uses to signal negative
                state. Neutral Cancel follows. */}
            <button
              type="button"
              onClick={onConfirmRemove}
              className={`border-0 mr-xs ${css({
                backgroundColor: palette.cayenne50,
                color: palette.white,
                borderRadius: '4px',
                padding: '6px 12px',
                fontSize: '14px',
                lineHeight: '20px',
                cursor: 'pointer',
                ':hover': { opacity: 0.9 },
                ':focus-visible': {
                  outline: `2px solid ${palette.belizeHole}`,
                  outlineOffset: '2px',
                },
              })}`}
            >
              {t('remove')}
            </button>
            <Button kind="minimal" size="small" onClick={onCancelRemove}>
              {t('cancel')}
            </Button>
          </div>
        ) : (
          <div className="d-flex align-items-center">
            <Button
              kind="minimal"
              className={`border-0 bg-transparent p-0 d-flex align-items-center ${css(
                {
                  // The default border reset removes the focus ring; add a
                  // visible focus indicator so keyboard users can see the
                  // control while tabbing through the row (WCAG 2.4.7).
                  ':focus-visible': {
                    outline: `2px solid ${palette.belizeHole}`,
                    outlineOffset: '2px',
                  },
                }
              )}`}
              // aria-label fully overrides the accessible name (it already
              // describes the action completely), so adding the visible
              // "FAVORITED" text below doesn't change what's announced —
              // it stays icon+text visually but a single named action for
              // assistive tech, same as before this redesign.
              aria-label={t('remove_favorite', { name: favorite.name })}
              // uikit Button doesn't type `title` as a top-level prop, but
              // overrides.BaseButton.props is spread directly onto the real
              // underlying <button> (see WorkspaceSearch.tsx's identical
              // role/aria-selected workaround) — a title still gives sighted
              // users a hover affordance consistent with the rest of this
              // app (WCAG 3.2.4 Consistent Identification), now that the
              // visible label is "FAVORITED" text, not just an icon.
              overrides={{
                BaseButton: {
                  props: {
                    title: t('remove_favorite', { name: favorite.name }),
                  },
                },
              }}
              onClick={onRequestRemove}
            >
              <Star
                fillColor={palette.eggYolk50}
                strokeColor={palette.grey70}
                className="mr-xxs"
              />
              <Text.BodyText5 color="primary" tag="span">
                {t('favorited')}
              </Text.BodyText5>
            </Button>
            {/* Purely decorative divider between the two controls. Text
                (uikit) doesn't forward arbitrary props like aria-hidden, so
                it's applied on a wrapping native span instead. */}
            <span aria-hidden="true" className="mx-xs">
              <Text.BodyText5 color="grey40" tag="span">
                |
              </Text.BodyText5>
            </span>
            <Button
              kind="minimal"
              className={`border-0 bg-transparent p-0 d-flex align-items-center ${css(
                {
                  gap: '4px',
                  ':focus-visible': {
                    outline: `2px solid ${palette.belizeHole}`,
                    outlineOffset: '2px',
                  },
                }
              )}`}
              aria-label={tRoot('open_report_named', { title: favorite.name })}
              onClick={() => onOpenFavorite(favorite)}
            >
              <Text.BodyText5 color="primary">
                {t('view_report')}
              </Text.BodyText5>
              <ChevronRight fillColor={palette.belizeHole} />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default FavoriteRow
